/**
 * Sentinel Chat Export PDF
 * Generates a professional PDF of AI chat conversations
 */

import React from 'react';
import { Document, Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer';
import { baseStyles, colors } from './styles';
import {
  ClassificationBanner,
  PageHeader,
  Section,
  Divider,
  PageFooter,
  EmptyState,
} from './PDFComponents';

// Additional styles specific to chat export
const styles = StyleSheet.create({
  messageContainer: {
    marginBottom: 12,
  },
  messageHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  userBadge: {
    backgroundColor: colors.primary,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 3,
  },
  assistantBadge: {
    backgroundColor: colors.secondary,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 3,
  },
  badgeText: {
    fontSize: 8,
    fontWeight: 'bold',
    color: colors.white,
  },
  timestamp: {
    fontSize: 8,
    color: colors.gray400,
    marginLeft: 8,
  },
  messageContent: {
    fontSize: 10,
    color: colors.gray700,
    lineHeight: 1.5,
    paddingLeft: 10,
    borderLeftWidth: 2,
    borderLeftColor: colors.gray200,
  },
  userMessage: {
    borderLeftColor: colors.primary,
  },
  assistantMessage: {
    borderLeftColor: colors.secondary,
  },
  contextBox: {
    backgroundColor: colors.gray100,
    borderRadius: 4,
    padding: 10,
    marginBottom: 15,
  },
  contextTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: colors.gray700,
    marginBottom: 4,
  },
  contextText: {
    fontSize: 9,
    color: colors.gray600,
    lineHeight: 1.4,
  },
  imageContainer: {
    marginVertical: 15,
    alignItems: 'center',
  },
  imageCaption: {
    fontSize: 8,
    color: colors.gray400,
    marginTop: 4,
    textAlign: 'center',
  },
  twoImageRow: {
    flexDirection: 'row',
    gap: 10,
    marginVertical: 15,
  },
  singleImage: {
    flex: 1,
    alignItems: 'center',
  },
});

// ============ TYPES ============

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string | Date;
}

export interface SentinelChatData {
  title: string;
  subtitle?: string;
  messages: ChatMessage[];
  additionalContext?: string;
  images?: {
    map?: string;
    graph?: string;
    timeline?: string;
  };
  classification?: string;
}

// ============ HELPER FUNCTIONS ============

/**
 * Strip markdown formatting and emojis from text
 */
const stripMarkdown = (text: string): string => {
  return text
    // Remove emojis
    .replace(/[\u{1F600}-\u{1F64F}]/gu, '')
    .replace(/[\u{1F300}-\u{1F5FF}]/gu, '')
    .replace(/[\u{1F680}-\u{1F6FF}]/gu, '')
    .replace(/[\u{1F1E0}-\u{1F1FF}]/gu, '')
    .replace(/[\u{2600}-\u{26FF}]/gu, '')
    .replace(/[\u{2700}-\u{27BF}]/gu, '')
    // Remove bold/italic markers
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    // Remove headers
    .replace(/^#{1,6}\s+/gm, '')
    // Remove bullet points (keep text)
    .replace(/^[\s]*[-*+]\s+/gm, '• ')
    // Remove code blocks
    .replace(/```[\s\S]*?```/g, '[code block]')
    .replace(/`([^`]+)`/g, '$1')
    // Remove links (keep text)
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // Clean up extra whitespace
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const formatTimestamp = (timestamp?: string | Date): string => {
  if (!timestamp) return '';
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// ============ MESSAGE COMPONENT ============

interface MessageProps {
  message: ChatMessage;
}

const Message: React.FC<MessageProps> = ({ message }) => {
  const isUser = message.role === 'user';
  const cleanContent = stripMarkdown(message.content);
  
  return (
    <View style={styles.messageContainer} wrap={false}>
      <View style={styles.messageHeader}>
        <View style={isUser ? styles.userBadge : styles.assistantBadge}>
          <Text style={styles.badgeText}>{isUser ? 'YOU' : 'SENTINEL'}</Text>
        </View>
        {message.timestamp && (
          <Text style={styles.timestamp}>{formatTimestamp(message.timestamp)}</Text>
        )}
      </View>
      <Text style={[styles.messageContent, isUser ? styles.userMessage : styles.assistantMessage]}>
        {cleanContent}
      </Text>
    </View>
  );
};

// ============ MAIN COMPONENT ============

export const SentinelChatPDF: React.FC<{ data: SentinelChatData }> = ({ data }) => {
  const {
    title,
    subtitle,
    messages,
    additionalContext,
    images,
    classification,
  } = data;

  return (
    <Document>
      <Page size="A4" style={classification ? baseStyles.pageWithBanner : baseStyles.page}>
        {classification && <ClassificationBanner classification={classification} position="top" />}
        
        <PageHeader 
          title={title}
          subtitle={subtitle}
          meta={`Exported: ${new Date().toLocaleString()}`}
        />

        {/* Additional Context */}
        {additionalContext && (
          <View style={styles.contextBox}>
            <Text style={styles.contextTitle}>Analysis Context</Text>
            <Text style={styles.contextText}>{additionalContext}</Text>
          </View>
        )}

        {/* Visual attachments */}
        {(images?.map || images?.graph || images?.timeline) && (
          <Section title="Visual Reference">
            {images.map && images.graph ? (
              <View style={styles.twoImageRow}>
                <View style={styles.singleImage}>
                  <Image src={images.graph} style={{ maxWidth: 250, maxHeight: 150, objectFit: 'contain' }} />
                  <Text style={styles.imageCaption}>Network Graph</Text>
                </View>
                <View style={styles.singleImage}>
                  <Image src={images.map} style={{ maxWidth: 250, maxHeight: 150, objectFit: 'contain' }} />
                  <Text style={styles.imageCaption}>Location Map</Text>
                </View>
              </View>
            ) : (images?.graph || images?.map) && (
              <View style={styles.imageContainer}>
                <Image 
                  src={images.graph || images.map} 
                  style={{ width: 500, height: 250, objectFit: 'contain' }} 
                />
                <Text style={styles.imageCaption}>
                  {images.graph ? 'Network Graph' : 'Location Map'}
                </Text>
              </View>
            )}
            
            {images?.timeline && (
              <View style={styles.imageContainer}>
                <Image src={images.timeline} style={{ width: 500, height: 180, objectFit: 'contain' }} />
                <Text style={styles.imageCaption}>Activity Timeline</Text>
              </View>
            )}
          </Section>
        )}

        <Divider />

        {/* Chat Messages */}
        <Section title={`Conversation (${messages.length} messages)`}>
          {messages.length === 0 ? (
            <EmptyState message="No messages in this conversation." />
          ) : (
            messages.map((msg, index) => (
              <Message key={index} message={msg} />
            ))
          )}
        </Section>

        <PageFooter />
        {classification && <ClassificationBanner classification={classification} position="bottom" />}
      </Page>
    </Document>
  );
};